import {Form} from 'antd';
import {useCallback} from 'react';
import {useMutation} from '@apollo/client';
import {toast} from 'react-toastify';
import {TRANSLATE_MULTIPLE_FIELDS} from 'graphql/queries/investorUpdates';

// Localized text fields on a full screen popup that can be auto-translated.
// popUpContent is rich-text HTML; the rest are plain strings. All go through the
// same html-treatment translate call.
const TRANSLATABLE_FIELDS = [
  'title',
  'subTitle',
  'headerBadgeText',
  'footerText',
  'closeButtonLabel',
  'actionButtonLabel',
  'popUpContent',
];

export const useFullScreenPopup = () => {
  const [form] = Form.useForm();

  const [translateText, {loading: loadingTranslation}] = useMutation(
    TRANSLATE_MULTIPLE_FIELDS,
  );

  // Translate the original (root-language) content into the selected
  // translation language and write the results into the `multilang` object.
  const handleTranslate = useCallback(async () => {
    const values = form.getFieldsValue(true);
    const sourceLang = values.language;
    const targetLang = values.translation;

    if (!sourceLang) {
      return toast.error('Select a language in the Settings step first');
    }
    if (!targetLang) {
      return toast.error('Select a translation language first');
    }

    const fieldsToTranslate = TRANSLATABLE_FIELDS.map(key => [
      key,
      values[key],
    ]).filter(([, text]) => text && `${text}`.trim() !== '');

    // Feature (benefit) labels — translated by index, icons stay as-is.
    const benefits = values.benefits || [];
    const benefitEntries = benefits
      .map((benefit, index) => [index, benefit?.label])
      .filter(([, label]) => label && `${label}`.trim() !== '');

    if (!fieldsToTranslate.length && !benefitEntries.length) {
      return toast.error('Add some content to translate first');
    }

    try {
      const {data} = await translateText({
        variables: {
          translations: [
            ...fieldsToTranslate.map(([, text]) => ({
              sourceLang,
              sourceText: text,
              targetLang,
            })),
            ...benefitEntries.map(([, label]) => ({
              sourceLang,
              sourceText: label,
              targetLang,
            })),
          ],
          treatment: 'html',
        },
      });

      const results = data?.translateText?.translations;
      if (!results?.length) {
        return toast.error('Something went wrong when translating');
      }

      // Set each translated field by explicit path. setFields (vs a single
      // setFieldsValue with a fresh object) reliably updates the mounted
      // Form.Items in the translated tab AND writes the store so the values are
      // submitted in multiLang.
      const fieldUpdates = [];
      fieldsToTranslate.forEach(([key], index) => {
        if (results[index] !== undefined) {
          fieldUpdates.push({name: ['multilang', key], value: results[index]});
        }
      });
      // Benefit (feature) label results come after the text fields; map them
      // back to their original index, leaving icons untouched.
      benefitEntries.forEach(([benefitIndex], order) => {
        const resultIndex = fieldsToTranslate.length + order;
        if (results[resultIndex] !== undefined) {
          fieldUpdates.push({
            name: ['multilang', 'benefits', benefitIndex, 'label'],
            value: results[resultIndex],
          });
        }
      });

      form.setFields(fieldUpdates);
      toast.success('Translation completed successfully!');
    } catch (error) {
      toast.error('Something went wrong during translation');
    }
  }, [form, translateText]);

  return {form, loadingTranslation, handleTranslate};
};
